import {
  type TboCityOptionList,
  TboCityOptionListSchema,
  type TboFlightFareQuoteInput,
  type TboFlightFareQuoteResponse,
  TboFlightFareQuoteResponseSchema,
  type TboFlightFareRuleInput,
  type TboFlightFareRuleResponse,
  TboFlightFareRuleResponseSchema,
  type TboFlightFareUpsellInput,
  type TboFlightFareUpsellResponse,
  TboFlightFareUpsellResponseSchema,
  type TboFlightSearchInput,
  type TboFlightSearchResponse,
  TboFlightSearchResponseSchema,
  type TboHotelLiveDetail,
  TboHotelLiveDetailSchema,
  type TboHotelLiveSearchInput,
  type TboHotelLiveSearchResponse,
  TboHotelLiveSearchResponseSchema,
  type TboHotelRoomsInput,
  type TboHotelRoomsResponse,
  TboHotelRoomsResponseSchema,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Live TBO search clients used by the itinerary builder (B5.E+). Hotels and
 * flights are searched against TBO in real time, mirroring the legacy builder.
 * All require TBO credentials in Secrets Manager server-side; the API returns
 * 503 when they are absent (surfaced as a toast by the calling hook).
 */

/** Live TBO searches hit a slow upstream and often fan out (many cities/legs),
 *  so they get a far longer deadline than the default 30s CRUD timeout. The
 *  backend caps each individual TBO call, so this is the aggregate ceiling. */
const LIVE_SEARCH_TIMEOUT = 150_000;

export const tboCityOptions = async (
  countryCode: string,
  q?: string,
): Promise<TboCityOptionList> => {
  const { data } = await apiClient.get<unknown>('/tbo/cities/options', {
    params: { countryCode, ...(q ? { q } : {}) },
  });
  return TboCityOptionListSchema.parse(data);
};

export const searchTboHotels = async (
  input: TboHotelLiveSearchInput,
): Promise<TboHotelLiveSearchResponse> => {
  const { data } = await apiClient.post<unknown>('/tbo-hotel/search', input, {
    timeout: LIVE_SEARCH_TIMEOUT,
  });
  return TboHotelLiveSearchResponseSchema.parse(data);
};

/**
 * Fast mirror-backed lookup of hotel codes for a TBO city. Used by the picker
 * to fan out parallel Search calls, one per batch, so results stream into the
 * UI as each batch resolves instead of waiting for a single 5+ minute batch.
 *
 * `minStarRating=3` matches the legacy Utazzo policy - 1/2-star inventory is
 * never quoted to customers. `offset` supports "Load remaining N hotels" - the
 * picker pages through codes[500..N] via a second fan-out on operator demand
 * so first paint stays fast on large cities (Da Nang: ~1400 3+star codes).
 *
 * `totalMatching` = count of codes matching the star filter, `totalMirrored`
 * = full mirror count (star-agnostic, for status messaging).
 */
export const fetchTboCityHotelCodes = async (
  tboCityId: string,
  limit = 500,
  opts: { minStarRating?: number; offset?: number; additionalTboCityIds?: string[] } = {},
): Promise<{ codes: string[]; totalMirrored: number; totalMatching: number }> => {
  const params: Record<string, string | number> = {
    tboCityId,
    limit,
    minStarRating: opts.minStarRating ?? 3,
  };
  if (opts.offset !== undefined && opts.offset !== null) params.offset = opts.offset;
  if (opts.additionalTboCityIds && opts.additionalTboCityIds.length > 0) {
    params.additionalTboCityIds = opts.additionalTboCityIds.join(',');
  }
  const { data } = await apiClient.get<{
    codes: string[];
    totalMirrored: number;
    totalMatching: number;
  }>('/tbo-hotel/city-hotel-codes', { params });
  return data;
};

export const fetchTboHotelRooms = async (
  input: TboHotelRoomsInput,
): Promise<TboHotelRoomsResponse> => {
  const { data } = await apiClient.post<unknown>('/tbo-hotel/rooms', input, {
    timeout: LIVE_SEARCH_TIMEOUT,
  });
  return TboHotelRoomsResponseSchema.parse(data);
};

export const fetchTboHotelLiveDetails = async (hotelCode: string): Promise<TboHotelLiveDetail> => {
  const { data } = await apiClient.get<unknown>(`/tbo-hotel/${hotelCode}/live`, {
    timeout: LIVE_SEARCH_TIMEOUT,
  });
  return TboHotelLiveDetailSchema.parse(data);
};

export const searchTboFlights = async (
  input: TboFlightSearchInput,
): Promise<TboFlightSearchResponse> => {
  const { data } = await apiClient.post<unknown>('/tbo-flight/search', input, {
    timeout: LIVE_SEARCH_TIMEOUT,
  });
  return TboFlightSearchResponseSchema.parse(data);
};

export const quoteTboFlight = async (
  input: TboFlightFareQuoteInput,
): Promise<TboFlightFareQuoteResponse> => {
  const { data } = await apiClient.post<unknown>('/tbo-flight/fare-quote', input, {
    timeout: LIVE_SEARCH_TIMEOUT,
  });
  return TboFlightFareQuoteResponseSchema.parse(data);
};

export const upsellTboFlight = async (
  input: TboFlightFareUpsellInput,
): Promise<TboFlightFareUpsellResponse> => {
  const { data } = await apiClient.post<unknown>('/tbo-flight/fare-upsell', input, {
    timeout: LIVE_SEARCH_TIMEOUT,
  });
  return TboFlightFareUpsellResponseSchema.parse(data);
};

export const ruleTboFlight = async (
  input: TboFlightFareRuleInput,
): Promise<TboFlightFareRuleResponse> => {
  const { data } = await apiClient.post<unknown>('/tbo-flight/fare-rule', input, {
    timeout: LIVE_SEARCH_TIMEOUT,
  });
  return TboFlightFareRuleResponseSchema.parse(data);
};
